import unescape from 'lodash/unescape';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import Config from 'global/common/Config';
import Clipboard from 'clipboard';
import DOMUtils from '@spothero/utils/dom';
import SearchUtils from 'search-utils';
import Button from 'button-component';
import Textfield from 'textfield-component';
import Tooltip from 'tooltip-component';
import {toggleListView} from '../../common/search-actions';

class SpotMapControls extends Component {
    constructor(props) {
        super(props);

        this._onShareUrlToggleClick = this._onShareUrlToggleClick.bind(this);
        this._onShareUrlClick = this._onShareUrlClick.bind(this);

        this.state = {
            showShareUrl: false,
            showShareUrlTooltip: false
        };
    }

    componentDidMount() {
        this._clipboard = new Clipboard('.SpotMap-share-url-toggle');
    }

    componentWillUnmount() {
        this._clipboard.destroy();

        if (this._shareUrlTooltipShowTimeout) {
            clearTimeout(this._shareUrlTooltipShowTimeout);
            clearTimeout(this._shareUrlTooltipHideTimeout);
        }
    }

    _onShareUrlToggleClick(evt) {
        this.setState({
            showShareUrl: !this.state.showShareUrl
        });

        this._shareUrlTooltipShowTimeout = setTimeout(() => {
            this.setState({
                showShareUrlTooltip: !this.state.showShareUrlTooltip
            });
        }, 250);

        this._shareUrlTooltipHideTimeout = setTimeout(() => {
            if (this._shareUrlTooltip) {
                const tooltipEl = this._shareUrlTooltip.refs.container;

                DOMUtils.removeClass(tooltipEl, 'Tooltip-showing');

                window.transitionEnd(tooltipEl).bind(() => {
                    window.transitionEnd(tooltipEl).unbind();

                    this.setState({
                        showShareUrlTooltip: !this.state.showShareUrlTooltip
                    });
                });
            }
        }, 2000);
    }

    _onShareUrlClick() {
        this._shareUrl.refs.input.input.select();
    }

    render() {
        const {
            isMobile,
            isDestinationOutOfBounds,
            isListViewActive,
            zoom,
            destinationTitle,
            onZoomIn,
            onZoomOut,
            onListViewToggle,
            onGoToCurrentLocation,
            onReturnToDestination,
            transientStarts,
            transientEnds,
            monthlyStarts,
            isMonthly,
            searchString,
            latitude,
            longitude
        } = this.props;
        const {
            showShareUrl,
            showShareUrlTooltip
        } = this.state;
        const shareUrlToggleClasses = classNames(
            'SpotMap-control',
            'SpotMap-share-url-toggle',
            {'SpotMap-control-active': showShareUrl}
        );
        const shareUrl = SearchUtils.getShareURL({
            transientStarts,
            transientEnds,
            monthlyStarts,
            isMonthly,
            searchString,
            latitude,
            longitude
        });

        return (
            <div className="SpotMapControls">
                {!isMobile &&
                    <div className="SpotMap-control SpotMap-zoom">
                        <Button
                            className="SpotMap-zoom-in"
                            icon="plus"
                            onClick={onZoomIn}
                            disabled={(zoom > 19)}
                        />
                        <Button
                            className="SpotMap-zoom-out"
                            icon="minus"
                            onClick={onZoomOut}
                            disabled={(zoom < 4)}
                        />
                    </div>
                }
                {(!isMobile && !isListViewActive) &&
                    <Button
                        className="SpotMap-control SpotMap-list-view-toggle"
                        icon="angle-double-right"
                        onClick={onListViewToggle}
                    />
                }
                {isMobile &&
                    <Button
                        className="SpotMap-control SpotMap-go-to-current-location"
                        icon="location-arrow"
                        onClick={onGoToCurrentLocation}
                    />
                }
                {!isMobile && isDestinationOutOfBounds &&
                    <Button
                        className="SpotMap-control SpotMap-return-to-destination"
                        icon="find-spot"
                        color="secondary"
                        width="narrow"
                        onClick={onReturnToDestination}
                    >
                        Return to {unescape(destinationTitle)}
                    </Button>
                }
                <Button
                    className={shareUrlToggleClasses}
                    icon="share"
                    onClick={this._onShareUrlToggleClick}
                    data-clipboard-text={shareUrl}
                />
                {showShareUrl &&
                    <span>
                        <Textfield
                            ref={node => { this._shareUrl = node; }}
                            className="SpotMap-share-url"
                            value={shareUrl}
                            onClick={this._onShareUrlClick}
                            size="lg"
                            select
                        />
                        {showShareUrlTooltip &&
                            <Tooltip
                                ref={node => { this._shareUrlTooltip = node; }}
                                showClose={false}
                                adjustArrow={false}
                                position="top"
                                triggerItem={`.SpotMap-share-url`}
                            >
                                Copied to Clipboard
                            </Tooltip>
                        }
                    </span>
                }
            </div>
        );
    }
}

SpotMapControls.propTypes = {
    isMobile: PropTypes.bool.isRequired,
    isDestinationOutOfBounds: PropTypes.bool.isRequired,
    isListViewActive: PropTypes.bool.isRequired,
    zoom: PropTypes.number.isRequired,
    destinationTitle: PropTypes.string.isRequired,
    onZoomIn: PropTypes.func.isRequired,
    onZoomOut: PropTypes.func.isRequired,
    onListViewToggle: PropTypes.func.isRequired,
    onGoToCurrentLocation: PropTypes.func.isRequired,
    onReturnToDestination: PropTypes.func.isRequired,
    transientStarts: PropTypes.string,
    transientEnds: PropTypes.string,
    monthlyStarts: PropTypes.string,
    isMonthly: PropTypes.bool,
    searchString: PropTypes.string,
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired
};

SpotMapControls.defaultProps = {
    isListViewActive: true
};

const mapStateToProps = state => {
    const {
        search: {
            data: {
                isListVisible,
                params: {
                    starts,
                    ends,
                    monthly_start_date,
                    monthly,
                    latitude,
                    longitude,
                    search_string
                }
            }
        }
    } = state;

    return {
        isListViewActive: isListVisible,
        transientStarts: starts,
        transientEnds: ends,
        monthlyStarts: monthly_start_date,
        isMonthly: monthly,
        searchString: search_string,
        latitude,
        longitude
    };
};

const mapDispatchToProps = {
    onListViewToggle: toggleListView
};

const enhance = compose(
    Config,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SpotMapControls);



// WEBPACK FOOTER //
// ./spothero/static/main/js/search/components/common/SpotMapControls.jsx